import React, { useEffect, useState, useRef } from "react";
import { Form, Row, Col, Container } from "react-bootstrap";
import PropTypes from 'prop-types';
import { customValidators, object, string, array as yuparray, number as yupnumber, date } from '../../../Helpers/Validators/customValidators';
import moment from 'moment';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import Button from '@material-ui/core/Button';
import BeneficiaryForm from './BeneficiaryForm';
import { TextInput, SwitchInput, SelectInput, StyledRadioInput } from "../../../Helpers/Inputs";
import BackNext from '../../Common/SubComponents/BackNext';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import { InputMaskZip } from '../../../Helpers/Formatters';
import { ConvertStringToBoolean, ConvertBooleanToString } from "../../../Helpers/Formatters/ConvertBoolean";
import GenerateUniqueSSN from '../../../Helpers/Prefill/GenerateUniqueSSN'
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.js';
import Alert from '@material-ui/lab/Alert';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import SvgIcon from '@material-ui/core/SvgIcon';
import Step6Icon from '../icons/step_6.svg';

export default function Beneficiary(props) {

  const { register, errors, handleSubmit, setValue, triggerValidation, reset } = useForm({
    validationSchema: Beneficiary.ValidationSchema,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    register({ name: 'collateralAssignment' });
    setValue('collateralAssignment', props.formState.collateralAssignment);

    register({ name: 'collateralAssigneeAddressState' });
    setValue('collateralAssigneeAddressState', props.formState.collateralAssigneeAddressState);

    register({ name: 'collateralAssigneeAddressZip' });
    setValue('collateralAssigneeAddressZip', props.formState.collateralAssigneeAddressZip);

    register({ name: 'emailCollateralCopy' });
    setValue('emailCollateralCopy', props.formState.emailCollateralCopy);

    props.formState.beneficiaries.forEach((bene, index) => {
      register({ name: `beneficiaries[${index}].state` });
      setValue(`beneficiaries[${index}].state`, bene.state);

      register({ name: `beneficiaries[${index}].beneficiary` });
      setValue(`beneficiaries[${index}].beneficiary`, bene.beneficiary);

      register({ name: `beneficiaries[${index}].relationship` });
      setValue(`beneficiaries[${index}].relationship`, bene.relationship);

      register({ name: `beneficiaries[${index}].suffix` });
      setValue(`beneficiaries[${index}].suffix`, bene.suffix);

      register({ name: `beneficiaries[${index}].ssnOrTaxpayerID` });
      setValue(`beneficiaries[${index}].ssnOrTaxpayerID`, bene.ssnOrTaxpayerID);

      register({ name: `beneficiaries[${index}].phoneNumber` });
      setValue(`beneficiaries[${index}].phoneNumber`, bene.phoneNumber);

      register({ name: `beneficiaries[${index}].dateOfBirth` });
      setValue(`beneficiaries[${index}].dateOfBirth`, bene.dateOfBirth);

      register({ name: `beneficiaries[${index}].trustDate` });
      setValue(`beneficiaries[${index}].trustDate`, bene.trustDate);

      register({ name: `beneficiaries[${index}].trustPhoneNumber` });
      setValue(`beneficiaries[${index}].trustPhoneNumber`, bene.trustPhoneNumber);

      register({ name: `beneficiaries[${index}].trustSsnTaxID` });
      setValue(`beneficiaries[${index}].trustSsnTaxID`, bene.trustSsnTaxID);

      register({ name: `beneficiaries[${index}].trustIdentifyOption` });
      setValue(`beneficiaries[${index}].trustIdentifyOption`, bene.trustIdentifyOption);

      register({ name: `beneficiaries[${index}].beneficiaryType` });
      setValue(`beneficiaries[${index}].beneficiaryType`, bene.beneficiaryType);

    });
  }, [register])

  const [showBeneCountMessage, setShowBeneCountMessage] = useState(false)
  const [beneCountMessage, setBeneCountMessage] = useState("")
  const beneErrorMessage = useRef()

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onFieldChange(field, value);
  };

  const submitForm = () => {
    let primaryBenePercentTotal = 0;
    let contingentBenePercentTotal = 0;
    const radix = 10;
    let primaryLength = 0;
    let contingentLength = 0;
    props.formState.beneficiaries.forEach((bene, index) => {
      primaryBenePercentTotal += bene.beneficiaryType === "Primary" ? parseInt(bene.percentage, radix) : 0;
      primaryLength += bene.beneficiaryType === "Primary" ? 1 : 0;
      contingentLength += bene.beneficiaryType === "Contingent " ? 1 : 0;
      contingentBenePercentTotal += bene.beneficiaryType === "Contingent " ? parseInt(bene.percentage, radix) : 0;
    });

    setShowBeneCountMessage(false);
    setBeneCountMessage("");
    if (primaryLength === 0) {
      setShowBeneCountMessage(true);
      setBeneCountMessage("At least 1 primary beneficiary required.");
      window.scrollTo(0, beneErrorMessage.current.offsetTop);
    } else if (primaryBenePercentTotal !== 100) {
      setShowBeneCountMessage(true);
      setBeneCountMessage("Primary beneficiary percentages must add up to 100");
      window.scrollTo(0, beneErrorMessage.current.offsetTop);
    } else if (contingentLength !== 0 && contingentBenePercentTotal !== 100) {
      setShowBeneCountMessage(true);
      setBeneCountMessage("Contingent beneficiary percentages must add up to 100");
      window.scrollTo(0, beneErrorMessage.current.offsetTop);
    }
    else {
      props.onNext()
    }
  }

  const addNewBeneficiary = () => {
    const newBeneIndex = props.formState.beneficiaries.length;

    register({ name: `beneficiaries[${newBeneIndex}].state` });
    register({ name: `beneficiaries[${newBeneIndex}].beneficiary` });
    register({ name: `beneficiaries[${newBeneIndex}].relationship` });
    register({ name: `beneficiaries[${newBeneIndex}].suffix` });
    register({ name: `beneficiaries[${newBeneIndex}].beneficiaryType` });
    const newForm = Beneficiary.DefaultFormState().beneficiaries[0];
    props.onFieldChange('beneficiaries', [...props.formState.beneficiaries, newForm]);
  }

  const forms = props.formState.beneficiaries.map((beneficiaryFormState, index) =>
    <BeneficiaryForm
      formState={beneficiaryFormState}
      onRemove={() => {
        props.onFieldChange(
          'beneficiaries',
          props.formState.beneficiaries.filter((form, formIndex) => formIndex !== index)
        );
        reset();
      }}
      onChange={(newForm) => {
        props.onFieldChange(
          'beneficiaries',
          props.formState.beneficiaries.map((form, formIndex) => {
            return formIndex === index ? newForm : form;
          })
        );
      }}
      setValue={setValue}
      trigger={triggerValidation}
      key={index}
      index={index}
      register={register}
      showBeneRemove={index !== 0}
      errors={errors.beneficiaries && errors.beneficiaries[index]}
      maritalStatus={props.maritalStatus}
    />
  )

  return (
    <Container>
      <form className="quote-form" onSubmit={handleSubmit(submitForm)}>
        <Row className="formPage justify-content-center">
          <Col lg={5}>
            <Row className="justify-content-center">
              <SvgIcon component={Step6Icon} className="headerIcon" />
            </Row>
            <h4 className="headerTitle">Protecting Your Mortgage or Business Loan</h4>
            <h5 className="headerSubtitle">This is the reason you are getting life insurance. Don't worry, you can change your beneficiaries at any time.</h5>

            {showBeneCountMessage && <div ref={beneErrorMessage} className="text-danger text-center mb-3">{beneCountMessage}</div>}

            <div className="switchContent pl-2">
              <Form.Group>
                <SwitchInput
                  name="collaterallyAssignPolicy"
                  id="collaterallyAssignPolicy"
                  label="Collaterally Assign this Policy"
                  checked={ConvertStringToBoolean(props.formState.collaterallyAssignPolicy)}
                  onChange={e => props.onFieldChange('collaterallyAssignPolicy', ConvertBooleanToString(e.target.checked))}
                  errors={errors.collaterallyAssignPolicy}
                  inputRef={register}
                  color="primary"
                  helpText="A collateral assignment conditionally appoints a lender as the primary beneficiary to use a life insurance death benefit as collateral."
                  switchColumnSize={8}
                />
              </Form.Group>
            </div>
          </Col>
        </Row>
        {props.formState.collaterallyAssignPolicy === "true" && (<div>
          <div className="formPageTitle">Collateral Assignment of Policy Form</div>
          <Row className="justify-content-center">
            <Col lg={5}>
              <Form.Group>
                <SelectInput
                  name="collateralAssignment"
                  id="collateralAssignment"
                  value={props.formState.collateralAssignment}
                  onChange={e => handleValueChange('collateralAssignment', e.target.value)}
                  label="Assignment"
                  inputRef={register}
                  selectOptions={[
                    { label: "Bank", value: "07" },
                    { label: "Company", value: "01" }]}
                  errors={errors.collateralAssignment}
                />
              </Form.Group>
              <Form.Group>
                <TextInput
                  name="collateralCompany"
                  id="collateralCompany"
                  label="Company/Bank Name"
                  value={props.formState.collateralCompany}
                  errors={errors.collateralCompany}
                  inputRef={register}
                  onChange={(e) => props.onFieldChange('collateralCompany', e.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <TextInput
                  name="collateralAssignee"
                  id="collateralAssignee"
                  label="Assignee Name"
                  value={props.formState.collateralAssignee}
                  onChange={e => props.onFieldChange('collateralAssignee', e.target.value)}
                  errors={errors.collateralAssignee}
                  inputRef={register}
                />
              </Form.Group>
              <Form.Group>
                <TextInput
                  name="collateralAssigneeAddressStreet"
                  id="collateralAssigneeAddressStreet"
                  label="Assignee Street Address"
                  value={props.formState.collateralAssigneeAddressStreet}
                  onChange={e => props.onFieldChange('collateralAssigneeAddressStreet', e.target.value)}
                  errors={errors.collateralAssigneeAddressStreet}
                  inputRef={register}
                />
              </Form.Group>
              <Form.Group>
                <TextInput
                  name="collateralAssigneeAddressCity"
                  id="collateralAssigneeAddressCity"
                  label="Assignee City"
                  value={props.formState.collateralAssigneeAddressCity}
                  onChange={e => props.onFieldChange('collateralAssigneeAddressCity', e.target.value)}
                  errors={errors.collateralAssigneeAddressCity}
                  inputRef={register}
                />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group>
                    <SelectInput
                      name="collateralAssigneeAddressState"
                      id="collateralAssigneeAddressState"
                      value={props.formState.collateralAssigneeAddressState}
                      onChange={(e) => handleValueChange('collateralAssigneeAddressState', e.target.value)}
                      label="Assignee State"
                      selectOptions={StatesWithDisabled(["NY"])}
                      errors={errors && errors.collateralAssigneeAddressState}
                      inputRef={register}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <TextInput
                      name="collateralAssigneeAddressZip"
                      id="collateralAssigneeAddressZip"
                      label="Assignee Zip"
                      value={props.formState.collateralAssigneeAddressZip}
                      onChange={e => handleValueChange('collateralAssigneeAddressZip', e.target.value)}
                      errors={errors.collateralAssigneeAddressZip}
                      inputRef={register}
                      InputProps={{
                        inputComponent: InputMaskZip,
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <TextInput
                  name="collateralAssigneeEmail"
                  id="collateralAssigneeEmail"
                  label="Assignee Email"
                  value={props.formState.collateralAssigneeEmail}
                  onChange={e => props.onFieldChange('collateralAssigneeEmail', e.target.value)}
                  errors={errors.collateralAssigneeEmail}
                  inputRef={register}
                />
              </Form.Group>

              {props.formState.collateralAssigneeEmail && (
                <Form.Group>
                  <StyledRadioInput
                    name="emailCollateralCopy"
                    id="emailCollateralCopy"
                    label="Do you want a copy of the collateral assignment sent to the assignee via email?"
                    value={props.formState.emailCollateralCopy}
                    options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                    onChange={e => handleValueChange('emailCollateralCopy', e)}
                    errors={errors.emailCollateralCopy}
                  />
                </Form.Group>
              )}

              <Form.Group>
                <TextInput
                  name="collateralLoanNumber"
                  id="collateralLoanNumber"
                  label="Loan Number"
                  value={props.formState.collateralLoanNumber}
                  onChange={e => props.onFieldChange('collateralLoanNumber', e.target.value)}
                  errors={errors.collateralLoanNumber}
                  inputRef={register}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>
        )}

        <Row className="justify-content-center">
          <Col lg={5}>
            <div className="formPageTitle">Beneficiary Information</div>

            <Alert className="mt-3 mb-3" severity="success" icon={<EmojiObjectsIcon />}>
              Insider Tip: The beneficiary (the one who receives the policy benefit) can be an individual, estate, or trust, and can be changed at any time during the life of the policy.
            </Alert>

            {forms}
            {props.formState.beneficiaries && props.formState.beneficiaries.length < 6 ? (
              <Button color="primary" onClick={() => addNewBeneficiary()} className="d-flex m-auto">
                ADD ANOTHER BENEFICIARY
              </Button>) :
              (
                <div className="text-danger text-center">No additional Beneficiaries may be added in the eApplication.  If additional Beneficiaries are required, please submit a paper application.</div>
              )}
          </Col>
        </Row>

        <Row className="justify-content-center mt-3">
          <Col lg={5}>
            <BackNext onBack={props.onBack}></BackNext>
          </Col>
        </Row>
      </form>
    </Container>
  )
}


Beneficiary.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

Beneficiary.ValidationSchema = object().shape({
  collaterallyAssignPolicy: string().required(),
  collateralAssignment: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: string().required('Assignment type is required.'),
    otherwise: string().notRequired()
  }),
  collateralCompany: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: string().required('Company Name is required.'),
    otherwise: string().notRequired()
  }),
  collateralAssignee: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: customValidators.string().isValidNameLimitedSpecialChar('Assignee Name is required.', true, false),
    otherwise: string().notRequired()
  }),
  collateralAssigneeAddressStreet: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: string()
      .isValidAddress('Invalid address. No special character is allowed')
      .required('Street Address is required.'),
    otherwise: string().notRequired()
  }),
  collateralAssigneeAddressCity: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: string().required('City is required.'),
    otherwise: string().notRequired()
  }),
  collateralAssigneeAddressState: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: string().required('State is required.'),
    otherwise: string().notRequired()
  }),
  collateralAssigneeAddressZip: string().when('collaterallyAssignPolicy', {
    is: "true",
    then: customValidators.string()
      .required('Zip is required.')
      .isValidNumbersOnly('Invalid zip.', 5),
    otherwise: string().notRequired()
  }),

  collateralAssigneeEmail: string()
    .email('Email address must be valid.')
    .notRequired(),

  beneficiaries: yuparray(object().shape({
    beneficiary: string()
      .required("Beneficiary is required."),
    firstName: string().when('beneficiary', {
      is: "Individual",
      then: customValidators.string()
        .isValidNameLimitedSpecialChar('First Name is required.', true, false),
      otherwise: string().notRequired()
    }),
    middleInitial: string()
      .matches(/^$|^(?!\s*$)[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]{1}$/, 'Must be single alpha character.')
      .notRequired(),
    lastName: string().when('beneficiary', {
      is: "Individual",
      then: customValidators.string()
        .isValidNameLimitedSpecialChar('Last Name is required.', true, false),
      otherwise: string().notRequired()
    }),
    ssnOrTaxpayerID: string().when('beneficiary', {
      is: "Individual",
      then: string()
        .matches(/(^(?:\d{3}-?\d{2}-?\d{4})?$)/, 'Social Security Number must be formatted as "123-45-6789".')
        .notRequired(),
      otherwise: string().notRequired()
    }),
    relationship: string().when('beneficiary', {
      is: "Individual",
      then: string().required('Relationship is required.'),
      otherwise: string().notRequired()
    }),
    dateOfBirth: date().when('beneficiary', {
      is: "Individual",
      then: date()
        .transform(emptyStringToUndefined)
        .typeError('Invalid date.')
        .max(moment(new Date()), 'Cannot be in future.')
        .notRequired(),
      otherwise: date().transform(emptyStringToUndefined).notRequired()
    }),
    phoneNumber: string().when('beneficiary', {
      is: "Individual",
      then: string()
        .matches(/^(?:(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4})?$/, 'Phone Number must be formatted as "(123) 456-7890".')
        .notRequired(),
      otherwise: string().notRequired()
    }),
    emailAddress: string().when('beneficiary', {
      is: "Individual",
      then: string().notRequired()
        .email('Email address must be valid.'),
      otherwise: string().notRequired()
    }),
    percentage: yupnumber()
      .typeError('Must be a number.')
      .transform(emptyStringToUndefined)
      .min(1)
      .max(100, 'Percentage must be between 1 and 100.')
      .required('Percentage is required.'),
    suffix: string().notRequired(),
    trustName: string().when('beneficiary', {
      is: "Trust",
      then: string().required('Trust Name is required.'),
      otherwise: string().notRequired()
    }),
    trustDate: date().when('beneficiary', {
      is: "Trust",
      then: date()
        .transform(emptyStringToUndefined)
        .typeError('Invalid date')
        .required('Trust Date is required.')
        .max(moment(new Date()), 'Trust date cannot be greater than current date.'),
      otherwise: date().transform(emptyStringToUndefined).notRequired()
    }),
    trustIdentifyOption: string().notRequired(),
    trustSsnTaxID: string().when('trustIdentifyOption', {
      is: "SSN",
      then: string().matches(/(^(?:\d{3}-?\d{2}-?\d{4})?$)/, 'Social Security Number must be formatted as "123-45-6789".').notRequired(),
      otherwise: string().matches(/(^(?:\d{2}-?\d{7})?$)/, 'Employer Identification Number must be formatted as "12-3456789".').notRequired(),
    }),
    trustEmail: string()
      .email('Email address must be valid.')
      .notRequired(),
    trustPhoneNumber: string()
      .matches(/^(?:(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4})?$/, 'Phone Number must be formatted as "(123) 456-7890".')
      .notRequired(),
  })
  )
});

Beneficiary.DefaultFormState = (prefill = false) => {
  return {
    useAsAnSBALoan: "false",
    collaterallyAssignPolicy: "false",
    collateralLoanNumber: prefill ? "12345" : "",
    collateralAssignment: prefill ? "01" : "",
    collateralCompany: prefill ? "Americo Financial" : "",
    collateralAssignee: prefill ? "Assignee Johnson" : "",
    collateralAssigneeAddressStreet: prefill ? "501 Covr St" : "",
    collateralAssigneeAddressCity: prefill ? "Boise" : "",
    collateralAssigneeAddressState: prefill ? "ID" : "",
    collateralAssigneeAddressZip: prefill ? "83705" : "",
    collateralAssigneeEmail: prefill ? "Johnson@example.com" : "",
    emailCollateralCopy: prefill ? "false" : "",

    beneficiaries: [
      {
        firstName: prefill ? 'Rebecca' : '',
        middleInitial: prefill ? 'S' : '',
        lastName: prefill ? 'Puga' : '',
        ssnOrTaxpayerID: prefill ? GenerateUniqueSSN('444') : "",
        relationship: prefill ? 'Parent' : '',
        dateOfBirth: prefill ? "09/19/1993" : "",
        phoneNumber: prefill ? "(123) 305-4567" : "",
        emailAddress: prefill ? "user.name@example.com" : "",
        percentage: prefill ? "100" : "",
        emailCollateralCopy: prefill ? "false" : "",
        beneficiary: prefill ? "Individual" : "",
        suffix: prefill ? "II" : "",
        trustName: prefill ? "Trust Demo" : "",
        trustDate: prefill ? "01/19/2020" : "",
        trustPhoneNumber: prefill ? "(321) 345-8908" : "",
        trustIdentifyOption: prefill ? "SSN" : "",
        trustSsnTaxID: prefill ? GenerateUniqueSSN('444') : "",
        trustEmail: prefill ? "trust@example.com" : "",
        beneficiaryType: "Primary"
      }
    ],
  }
}



// WEBPACK FOOTER //
// ./src/components/Quoters/Americo/steps/Beneficiary.js